/**
 * Панель пула на карточке байка — то, ради чего водитель открывает ссылку
 * из Telegram: номер в маршруте, заряд, статус, время в пуле и крупные
 * кнопки «Сирена», «Открыть»/«Закрыть», «Забрал», «Поставил», «Убрать из
 * пула», «2ГИС».
 *
 * Показывается только у байка из СВОЕГО пула (`poolPanelFor`), сверху
 * карточки. Замок здесь — тот же `BikeLock`, что ниже на карточке: второй
 * экземпляр карточка в этом случае не рисует.
 *
 * Кнопки пула ведёт `state` с поправкой на очередь (`effectivePoolState`),
 * а не голый ответ сервера: «Поставил» без сети ложится в очередь, и
 * рефетч пула до досылки вернул бы кнопки под второй тап.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { commandErrorText, useSiren } from '@/api/device'
import { poolItemNumber, usePoolAction, type PoolActionKind, type PoolItem } from '@/api/pool'
import { displayNumber, type BikeView } from '@/lib/bike-view'
import { batteryText } from '@/lib/describe-bike'
import { hasPosition, type LatLng } from '@/lib/geo'
import { poolDurationText } from '@/lib/pool'
import { navUrl } from '@/lib/navigation'
import { vehicleStatusLabel } from '@/lib/vehicle-status'
import { BigButton, Notice } from '@/components/ui'
import { BikeLock } from './bike-lock'
import { DeliverSheet, DropSheet } from './pool-sheets'

const SIREN_SECONDS = 60

function titleFor(item: PoolItem, state: string): string {
  if (state === 'closing') return 'Пул · ждёт отправки'
  if (state === 'picked') return 'Пул · в машине'
  return item.position > 0 ? `Пул · №${item.position} в маршруте` : 'Пул'
}

export function PoolPanel({
  item,
  state,
  view,
  me,
  online,
}: {
  item: PoolItem
  /** `effectivePoolState`: серверное состояние с поправкой на очередь. */
  state: string
  view: BikeView
  me: LatLng | null
  online: boolean
}) {
  const action = usePoolAction()
  const siren = useSiren(view.vehicleId)
  const [sheet, setSheet] = useState<'deliver' | 'drop' | null>(null)
  const number = displayNumber(view) || poolItemNumber(item)
  const inPool = poolDurationText(item.addedAt || view.vehicle.poolSince)

  function send(kind: PoolActionKind, payload: Record<string, unknown>, done: string) {
    action.mutate(
      { kind, vehicleId: view.vehicleId, payload },
      {
        onSuccess: () => {
          toast(done)
        },
      },
    )
  }

  const here = me ? { lat: me.lat, lng: me.lng } : {}

  return (
    <section
      aria-label="Пул"
      className="mb-4 rounded-2xl border border-orange-400/50 bg-orange-400/10 p-4"
    >
      <h2 className="text-lg font-bold text-orange-200">{titleFor(item, state)}</h2>
      <p className="mt-1 text-sm opacity-80">
        №{number} · {batteryText(view)} · {vehicleStatusLabel[view.status] ?? view.status}
        {inPool ? ` · ${inPool}` : ''}
      </p>

      {state === 'closing' ? (
        <div className="mt-2">
          <Notice tone="info">Отметка ждёт отправки — уйдёт, как появится сеть.</Notice>
        </div>
      ) : null}

      <div className="mt-3 flex gap-2">
        <BigButton
          tone="neutral"
          disabled={!online}
          busy={siren.isPending}
          onClick={() => {
            siren.mutate(SIREN_SECONDS, {
              onSuccess: () => {
                toast('Сирена включена на 60 с')
              },
              onError: (e) => {
                toast.error(commandErrorText(e))
              },
            })
          }}
        >
          Сирена
        </BigButton>
        {hasPosition(view) ? (
          <a
            href={navUrl(view)}
            target="_blank"
            rel="noreferrer"
            className="flex min-h-[56px] flex-1 items-center justify-center rounded-xl border
              border-white/20 px-4 font-medium"
          >
            2ГИС
          </a>
        ) : null}
      </div>

      <div className="mt-3">
        <BikeLock vehicleId={view.vehicleId} online={online} rentalBlocked={null} />
      </div>

      {state === 'pooled' || state === 'picked' ? (
        <div className="flex gap-2">
          {state === 'pooled' ? (
            <BigButton
              tone="primary"
              busy={action.isPending && action.variables.kind === 'pool_pick'}
              onClick={() => {
                send('pool_pick', here, 'Забрал — отмечено')
              }}
            >
              Забрал
            </BigButton>
          ) : null}
          <BigButton
            tone="primary"
            onClick={() => {
              setSheet('deliver')
            }}
          >
            Поставил
          </BigButton>
        </div>
      ) : null}

      {state === 'pooled' ? (
        <div className="mt-2 flex">
          <BigButton
            tone="neutral"
            onClick={() => {
              setSheet('drop')
            }}
          >
            Убрать из пула
          </BigButton>
        </div>
      ) : null}

      {action.isError ? (
        <div className="mt-2">
          <Notice tone="error">
            Отметка не сохранена на устройстве. Проверьте, что браузеру разрешено хранить
            данные, и повторите.
          </Notice>
        </div>
      ) : null}

      <DeliverSheet
        open={sheet === 'deliver'}
        onClose={() => {
          setSheet(null)
        }}
        me={me}
        destination={item.destination}
        onDeliver={(payload) => {
          setSheet(null)
          send('pool_deliver', payload, 'Поставил — отмечено')
        }}
      />
      <DropSheet
        open={sheet === 'drop'}
        onClose={() => {
          setSheet(null)
        }}
        number={number}
        me={me}
        onDrop={(payload) => {
          send('pool_drop', payload, 'Убран из пула')
        }}
      />
    </section>
  )
}
